import fetch from 'cross-fetch'
import StatusCodes from 'http-status-codes'
import i18next from 'i18next'

import { getSessionId, getTraceId } from '../../util/sentry'

type I18NextType = typeof i18next

type Options = {
  method?: string
  body?: object | null
  headers?: Record<string, string>
  getToken?: () => Promise<string | null>
}

export interface Error {
  code: string
  error: string
  message: string
  statusCode: number
}

export type ResponseSuccess<R> = {
  status: number
  data: R
  error: null
}

type ResponseFailure<R> = {
  status: number
  data: null
  error: R & Error
}

export type Res<R> = ResponseSuccess<R> | ResponseFailure<R>

export type Req = <R = object>(endpoint: string, opts?: Options) => Promise<Res<R>>

async function getJson(response: Response) {
  if (response.status === StatusCodes.NO_CONTENT) return null

  try {
    return await response.json()
  } catch (err) {
    console.warn('🔴 Error parsing JSON from response.', err)
    return null
  }
}

const makeRequest: (
  baseUrl: string,
  options: {
    includeLocale?: boolean
    i18n?: I18NextType
    extraHeaders?: Record<string, string>
    sendTracingHeaders?: boolean
  }
) => Req =
  (baseUrl, { includeLocale, i18n = i18next, sendTracingHeaders = false } = {}) =>
  async (endpoint, opts = {}) => {
    const hasBody = opts.body !== undefined

    const resp = await fetch(`${baseUrl}${endpoint}`, {
      method: opts.method || (hasBody ? 'POST' : 'GET'),
      body: JSON.stringify(opts.body),
      headers: {
        ...(hasBody && {
          'Content-Type': 'application/json',
        }),
        ...(opts?.getToken && {
          Authorization: `Bearer ${await opts.getToken()}`,
        }),
        ...(includeLocale && { 'x-locale': i18n.language }),
        ...opts?.headers,
        ...(sendTracingHeaders && {
          'x-session-id': await getSessionId(),
          'x-trace-id': getTraceId(),
          'x-app': 'patient',
        }),
      },
    })

    const contentType = resp.headers.get('Content-Type') || ''
    const isSuccess = resp.ok

    // Handle response as JSON or binary (PDF)
    let data: any
    if (contentType.includes('application/json')) {
      data = await getJson(resp)
    } else if (contentType.includes('application/pdf')) {
      data = await resp.blob() // Handle binary data like PDFs
    } else {
      data = await resp.text() // Handle other text-based responses
    }

    return {
      status: resp.status,
      data: isSuccess ? data : null,
      error: isSuccess ? null : data,
    }
  }

export default makeRequest
